import React from 'react';
import css from './ShopSettingsPage.module.css';
import FooterContainer from '../FooterContainer/FooterContainer';
import { LayoutSingleColumn, Page, UserNav } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import ShopSettingsForm from './ShopSettingsForm/ShopSettingsForm';
import { currentUserSelector } from '../../ducks/user.duck';
import {
  getCoverImage,
  getLastLogin,
  getProfileImageUrl,
  getUserBio,
  getUsername,
  isPaypalConnected,
  isShopifyConnected,
} from '../../util/dataExtractor';
import {
  listingsSelector,
  queryInProgressSelector,
  reviewsSelector,
  uploadImageInProgressSelector,
  updateCountryAndListings, // ⬅️ NEW IMPORT
} from './ShopSettingsPage.duck';
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom';
import { categorizeListings } from '../../util/helperFunctions';
import { withViewport } from '../../util/uiHelpers';
import { compose } from 'redux';
import { updateProfile } from '../ProfileSettingsPage/ProfileSettingsPage.duck';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { euCountries } from '../../translations/countryCodes';

// Normalize country to a single key (supports string or array)
// Normalize country to a single string value for DB (e.g. "BE")
const normalizeCountry = value => {
  if (!value) return null;

  // If react-select / FieldMultiSelect returns an array, use first item
  if (Array.isArray(value)) {
    const first = value[0];
    if (!first) return null;
    value = first;
  }

  if (typeof value === 'object') {
    // Prefer .value, then .code, then .key
    return value.value || value.code || value.key || null;
  }

  // Already a primitive (string like "BE")
  return value;
};

export const ShopSettingsPageComponent = props => {
  const { viewport } = props;
  const isMobileLayout = viewport?.width && viewport?.width > 0 && viewport?.width < 768;

  const intl = useIntl();
  const routeConfiguration = useRouteConfiguration();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  const schemaTitle = intl.formatMessage({
    id: 'ShopSettingsPage.title',
  });

  const scrollingDisabled = useSelector(isScrollingDisabled);
  const listings = useSelector(listingsSelector);
  const queryInProgress = useSelector(queryInProgressSelector);
  const currentUser = useSelector(currentUserSelector);
  const uploadImageInProgress = useSelector(uploadImageInProgressSelector);
  const reviews = useSelector(reviewsSelector);

  const userBio = getUserBio(currentUser);
  const username = getUsername(currentUser);
  const lastLogin = getLastLogin(currentUser);
  const coverPhoto = getCoverImage(currentUser);
  const profilePhoto = getProfileImageUrl(currentUser);
  const profileCreatedAt = currentUser?.attributes?.createdAt;

  const existingPublicData = currentUser?.attributes?.profile?.publicData || {};
  const existingCountryRaw = existingPublicData.country || null;

  const country = existingCountryRaw || null;
  const stripeConnected = !!currentUser?.stripeAccount?.id;
  const paypalConnected = isPaypalConnected(currentUser);
  const hasPaymentMethodConnected = stripeConnected || paypalConnected;
  const initialValues = {
    bio: userBio,
    coverPhoto,
    profilePhoto,
    username,
    country,
  };

  const { draftListings, publishedListings, closedListings } = categorizeListings(listings);

  const shopifyConnected = isShopifyConnected(currentUser);

  const handleSubmit = values => {
    const { bio, username: updatedUsername } = values;
    const updatedCountry = normalizeCountry(values.country);
    const previousCountry = normalizeCountry(existingCountryRaw);
    const isEUCountry = euCountries.includes(updatedCountry);
    const publicData = {
      ...existingPublicData,
      country: updatedCountry || null,
      isEUCountry,
    };

    const profile = {
      bio,
      displayName: updatedUsername || username,
      publicData,
    };

    const countryChanged = updatedCountry !== previousCountry;

    // If country changed, update profile + all listings' publicData.country
    if (countryChanged) {
      return dispatch(
        updateCountryAndListings({
          profile,
          country: updatedCountry || null, // e.g. 'GB'
          isEUCountry,
        })
      );
    }

    // If country didn't change, just update profile (bio/displayName/etc.)
    return dispatch(updateProfile(profile));
  };

  return (
    <Page scrollingDisabled={scrollingDisabled} title={schemaTitle}>
      <LayoutSingleColumn
        topbar={
          <>
            <TopbarContainer />
            <UserNav currentPage="ShopSettingsPage" />
          </>
        }
        footer={<FooterContainer />}
      >
        <div className={css.shopSettingsForm}>
          <ShopSettingsForm
            initialValues={initialValues}
            onSubmit={handleSubmit}
            profileCreatedAt={profileCreatedAt}
            lastLogin={lastLogin}
            uploadImageInProgress={uploadImageInProgress}
            reviews={reviews}
            history={history}
            routeConfiguration={routeConfiguration}
            location={location}
            listings={listings}
            listingsAreLoading={queryInProgress}
            draftListings={draftListings}
            publishedListings={publishedListings}
            closedListings={closedListings}
            isMobileLayout={isMobileLayout}
            hasPaymentMethodConnected={hasPaymentMethodConnected}
            shopifyConnected={shopifyConnected}
          />
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const ShopSettingsPage = compose(withViewport)(ShopSettingsPageComponent);

export default ShopSettingsPage;
